import { useEffect, useRef, useState, useSyncExternalStore, type ReactElement, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import ConfirmModal from '../components/ConfirmModal';
import { getModalPortalTarget } from '../lib/portalTarget';
import { currentDialog, settleDialog, subscribeDialogs, type DialogRequest, type NotifyKind } from './dialogStore';

// Über Dialogen (z-50); Rückfragen nutzen ConfirmModal mit layer="top" (70).
const LAYER = 70;
const BTN = 'px-4 py-3 sm:py-2 rounded min-h-[44px] inline-flex items-center justify-center gap-2 disabled:opacity-50';

const KIND_ICON: Record<NotifyKind, ReactElement> = {
  info: <Info size={20} className="text-blue-400 flex-shrink-0" />,
  success: <CheckCircle2 size={20} className="text-brand-400 flex-shrink-0" />,
  error: <AlertTriangle size={20} className="text-rose-400 flex-shrink-0" />,
};

function Frame({ title, icon, onBackdrop, children, footer }: {
  title: string; icon?: ReactElement; onBackdrop: () => void; children: ReactNode; footer: ReactNode;
}) {
  return createPortal(
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4" style={{ zIndex: LAYER }} onClick={onBackdrop} data-test-anchor="dialog-host">
      <div role="alertdialog" aria-modal="true" aria-label={title} className="bg-slate-900 border border-slate-800 rounded-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 p-4 border-b border-slate-800">
          {icon}
          <h3 className="font-semibold text-slate-100">{title}</h3>
        </div>
        <div className="p-4 space-y-3">{children}</div>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 p-4 border-t border-slate-800">{footer}</div>
      </div>
    </div>,
    getModalPortalTarget(),
  );
}

function NotifyDialog({ req }: { req: Extract<DialogRequest, { type: 'notify' }> }) {
  const o = req.options;
  const close = () => settleDialog(req.id, null);
  const btn = useRef<HTMLButtonElement>(null);
  useEffect(() => { btn.current?.focus(); }, []);
  return (
    <Frame
      title={o.title}
      icon={KIND_ICON[o.kind ?? 'info']}
      onBackdrop={close}
      footer={<button ref={btn} type="button" onClick={close} className={`${BTN} bg-brand-600 hover:bg-brand-500 text-primary-foreground`} data-action="close">{o.closeLabel ?? 'Verstanden'}</button>}
    >
      {o.message && <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-line">{o.message}</p>}
      {o.items && o.items.length > 0 && (
        <ul className="list-disc pl-5 text-sm text-slate-300 space-y-1">
          {o.items.map((it) => <li key={it}>{it}</li>)}
        </ul>
      )}
    </Frame>
  );
}

function PromptDialog({ req }: { req: Extract<DialogRequest, { type: 'prompt' }> }) {
  const o = req.options;
  const [text, setText] = useState(o.initialValue ?? '');
  const field = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  useEffect(() => { field.current?.focus(); field.current?.select(); }, []);

  const cancel = () => settleDialog(req.id, null);
  const valid = o.allowEmpty === true || text.trim().length > 0;
  const submit = () => { if (valid) settleDialog(req.id, o.multiline ? text : text.trim()); };
  // 16px Schrift: iOS zoomt sonst beim Antippen ins Feld.
  const fieldCls = 'w-full rounded bg-slate-950 border border-slate-700 text-slate-100 px-3 py-2 text-base focus:outline-none focus:border-brand-500';

  return (
    <Frame
      title={o.title}
      onBackdrop={() => { if (text === (o.initialValue ?? '')) cancel(); }}
      footer={(
        <>
          <button type="button" onClick={cancel} className={`${BTN} text-slate-300 hover:bg-slate-800`} data-action="cancel">{o.cancelLabel ?? 'Abbrechen'}</button>
          <button type="button" onClick={submit} disabled={!valid} className={`${BTN} bg-brand-600 hover:bg-brand-500 text-primary-foreground`} data-action="confirm">{o.confirmLabel ?? 'Übernehmen'}</button>
        </>
      )}
    >
      {o.message && <p className="text-sm text-slate-300 leading-relaxed">{o.message}</p>}
      <label className="block space-y-1">
        {o.fieldLabel && <span className="text-sm text-slate-400">{o.fieldLabel}</span>}
        {o.multiline
          ? <textarea ref={field} rows={4} value={text} maxLength={o.maxLength} placeholder={o.placeholder} onChange={(e) => setText(e.target.value)} className={fieldCls} />
          : (
            <input
              ref={field}
              type="text"
              value={text}
              maxLength={o.maxLength}
              placeholder={o.placeholder}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
              className={fieldCls}
            />
          )}
      </label>
    </Frame>
  );
}

/**
 * Zeigt Meldungen und Rückfragen aus `dialogs` / `useDialogs()`. Genau einmal in der
 * Portal-Shell eingebaut.
 */
export function DialogHost() {
  const req = useSyncExternalStore(subscribeDialogs, currentDialog, currentDialog);

  useEffect(() => {
    if (!req) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Vor den Dialogen darunter abfangen — Escape gilt der Meldung.
      e.stopImmediatePropagation();
      settleDialog(req.id, req.type === 'confirm' ? false : null);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [req]);

  if (!req) return null;
  if (req.type === 'notify') return <NotifyDialog key={req.id} req={req} />;
  if (req.type === 'prompt') return <PromptDialog key={req.id} req={req} />;
  const o = req.options;
  return (
    <ConfirmModal
      key={req.id}
      open
      layer="top"
      title={o.title}
      message={o.message}
      confirmLabel={o.confirmLabel ?? 'OK'}
      cancelLabel={o.cancelLabel ?? 'Abbrechen'}
      danger={o.danger}
      onClose={() => settleDialog(req.id, false)}
      onConfirm={() => settleDialog(req.id, true)}
    />
  );
}
